import { useState, useMemo } from "react";
import { useWorkflowRuns, useBuildsStats, useGitHubCollectorStatus } from "../hooks/useGitHub";
import PageHeader from "../components/common/PageHeader";
import StatusBadge from "../components/common/StatusBadge";
import MetricCard from "../components/common/MetricCard";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBanner from "../components/common/ErrorBanner";
import EmptyState from "../components/common/EmptyState";
import {
  CheckCircle2,
  XCircle,
  Clock,
  GitBranch,
  ExternalLink,
  RefreshCw,
  Hammer,
  FolderGit2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function periodDatesToParams(days: number): { date_from?: string; date_to?: string } {
  if (days === 0) return {};
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return {
    date_from: start.toISOString(),
    date_to: end.toISOString(),
  };
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.round((now - then) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export default function Builds() {
  const [periodDays, setPeriodDays] = useState(0);
  const [status, setStatus] = useState("");
  const [conclusion, setConclusion] = useState("");
  const [event, setEvent] = useState("");
  const [branch, setBranch] = useState("");
  const [actorLogin, setActorLogin] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const dateRange = useMemo(() => periodDatesToParams(periodDays), [periodDays]);
  const { data: syncStatus } = useGitHubCollectorStatus();

  const runsParams = useMemo(
    () => ({
      ...dateRange,
      ...(status ? { status } : {}),
      ...(conclusion ? { conclusion } : {}),
      ...(event ? { event } : {}),
      ...(branch ? { branch } : {}),
      ...(actorLogin ? { actor_login: actorLogin } : {}),
      page,
      page_size: pageSize,
    }),
    [dateRange, status, conclusion, event, branch, actorLogin, page]
  );

  const {
    data: runsData,
    isLoading: runsLoading,
    error: runsError,
    refetch,
  } = useWorkflowRuns(runsParams);

  const { data: stats } = useBuildsStats(dateRange);

  const runs = runsData?.items ?? [];
  const total = runsData?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  const resetFilters = () => {
    setStatus("");
    setConclusion("");
    setEvent("");
    setBranch("");
    setActorLogin("");
    setPage(1);
  };

  if (runsError)
    return <ErrorBanner message="Failed to load builds" onRetry={refetch} />;

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header */}
      <PageHeader
        title="Builds"
        description="Workflow runs across all repositories."
      >
        <div className="flex items-center gap-3">
          <select
            value={periodDays}
            onChange={(e) => {
              setPeriodDays(Number(e.target.value));
              setPage(1);
            }}
            className="select-field"
          >
            <option value={0}>All time</option>
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={() => refetch()}
            className="btn-secondary inline-flex items-center gap-1.5"
          >
            <RefreshCw className={`h-4 w-4 ${runsLoading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </PageHeader>

      {/* GitHub sync status banner */}
      {syncStatus && (
        <div
          className={`rounded-2xl border px-4 py-2.5 text-sm flex items-center gap-3 ${
            syncStatus.is_healthy
              ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300"
              : "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300"
          }`}
        >
          <div className="flex items-center gap-1.5">
            <span
              className={`inline-block h-2 w-2 rounded-full ${
                syncStatus.is_healthy
                  ? "bg-emerald-500"
                  : syncStatus.last_sync_status === "running"
                    ? "bg-blue-500 animate-pulse"
                    : "bg-amber-500"
              }`}
            />
            <span className="font-medium">GitHub sync</span>
          </div>
          <span className="text-slate-400 dark:text-slate-500">•</span>
          <span>Last sync: {formatRelative(syncStatus.last_sync_at)}</span>
          <span className="text-slate-400 dark:text-slate-500">•</span>
          <span>{syncStatus.items_synced.toLocaleString()} items synced</span>
          {syncStatus.last_sync_status && (
            <>
              <span className="text-slate-400 dark:text-slate-500">•</span>
              <span className="capitalize">{syncStatus.last_sync_status}</span>
            </>
          )}
          <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">
            Auto-refreshes every 15s
          </span>
        </div>
      )}

      {/* Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <MetricCard
          label="Total Builds"
          value={stats?.total_builds ?? 0}
          icon={<Hammer className="h-5 w-5" />}
        />
        <MetricCard
          label="Repositories"
          value={stats?.total_repos ?? 0}
          icon={<FolderGit2 className="h-5 w-5" />}
        />
        <MetricCard
          label="Successes"
          value={stats?.success_count ?? 0}
          subtext={stats ? `${stats.success_rate_pct}% success rate` : undefined}
          icon={<CheckCircle2 className="h-5 w-5 text-emerald-600" />}
        />
        <MetricCard
          label="Failures"
          value={stats?.failure_count ?? 0}
          icon={<XCircle className="h-5 w-5 text-red-500" />}
        />
      </div>

      {/* Branch Breakdown Cards */}
      {stats && (stats.branch_build_stats.length > 0 || stats.branch_pr_stats.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {stats.branch_build_stats.length > 0 && (
            <div className="card overflow-hidden">
              <div className="card-header">
                <h3 className="text-card-title text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <GitBranch className="h-4 w-4 text-indigo-500" />
                  Builds by Branch
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-surface-50 dark:bg-slate-800/60 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-400">
                    <tr>
                      <th className="px-4 py-2">Branch</th>
                      <th className="px-4 py-2 text-right">Builds</th>
                      <th className="px-4 py-2 text-right">Success</th>
                      <th className="px-4 py-2 text-right">Failure</th>
                      <th className="px-4 py-2 text-right" title="Cancelled, skipped, in progress, timed out, etc.">Other</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                    {stats.branch_build_stats.map((b) => (
                      <tr key={b.branch} className="hover:bg-surface-50 dark:hover:bg-slate-800/30 transition-colors">
                        <td className="px-4 py-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400">
                            {b.branch}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right font-medium text-slate-900 dark:text-slate-100">{b.total_builds}</td>
                        <td className="px-4 py-2 text-right text-emerald-600 dark:text-emerald-400">{b.success_count}</td>
                        <td className="px-4 py-2 text-right text-red-600 dark:text-red-400">{b.failure_count}</td>
                        <td className="px-4 py-2 text-right text-slate-500 dark:text-slate-400">{b.other_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {stats.branch_pr_stats.length > 0 && (
            <div className="card overflow-hidden">
              <div className="card-header">
                <h3 className="text-card-title text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <GitBranch className="h-4 w-4 text-emerald-500" />
                  PR Activity by Branch
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-surface-50 dark:bg-slate-800/60 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-400">
                    <tr>
                      <th className="px-4 py-2">Branch</th>
                      <th className="px-4 py-2 text-right">Merges</th>
                      <th className="px-4 py-2 text-right">Comments</th>
                      <th className="px-4 py-2 text-right">Reviews</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                    {stats.branch_pr_stats.map((b) => (
                      <tr key={b.branch} className="hover:bg-surface-50 dark:hover:bg-slate-800/30 transition-colors">
                        <td className="px-4 py-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
                            {b.branch}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right font-medium text-slate-900 dark:text-slate-100">{b.merges}</td>
                        <td className="px-4 py-2 text-right text-slate-600 dark:text-slate-400">{b.comments}</td>
                        <td className="px-4 py-2 text-right text-slate-600 dark:text-slate-400">{b.reviews}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="card card-body">
        <div className="flex flex-wrap items-center gap-3">
          <select value={event} onChange={(e) => { setEvent(e.target.value); setPage(1); }} className="select-field">
            <option value="">All Events</option>
            <option value="push">Push</option>
            <option value="pull_request">Pull Request</option>
            <option value="schedule">Schedule</option>
            <option value="workflow_dispatch">Manual</option>
          </select>

          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="select-field">
            <option value="">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="in_progress">In Progress</option>
            <option value="queued">Queued</option>
          </select>

          <select value={conclusion} onChange={(e) => { setConclusion(e.target.value); setPage(1); }} className="select-field">
            <option value="">All Conclusions</option>
            <option value="success">Success</option>
            <option value="failure">Failure</option>
            <option value="cancelled">Cancelled</option>
            <option value="skipped">Skipped</option>
          </select>

          <input
            type="text"
            placeholder="Branch..."
            value={branch}
            onChange={(e) => { setBranch(e.target.value); setPage(1); }}
            className="input-field w-32"
          />
          <input
            type="text"
            placeholder="Developer..."
            value={actorLogin}
            onChange={(e) => { setActorLogin(e.target.value); setPage(1); }}
            className="input-field w-36"
          />
          <button
            onClick={resetFilters}
            className="text-sm text-slate-500 dark:text-slate-400 hover:text-brand-600 dark:hover:text-brand-400 underline transition-colors"
          >
            Reset
          </button>
          <span className="ml-auto text-xs text-slate-400 dark:text-slate-500">{total} result{total !== 1 ? "s" : ""}</span>
        </div>
      </div>

      {/* Workflow Runs Table */}
      {runsLoading ? (
        <LoadingSpinner message="Loading builds..." />
      ) : runs.length === 0 ? (
        <EmptyState title="No builds found" description="No workflow runs match the current filters." />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-50 dark:bg-slate-800/60 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-400">
                <tr>
                  <th className="px-3 py-3 w-8"></th>
                  <th className="px-3 py-3">Workflow</th>
                  <th className="px-3 py-3">Repository</th>
                  <th className="px-3 py-3">Branch</th>
                  <th className="px-3 py-3">Event</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3">Developer</th>
                  <th className="px-3 py-3">Duration</th>
                  <th className="px-3 py-3">When</th>
                  <th className="px-3 py-3 w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                {runs.map((run) => {
                  const conclusionOrStatus = run.conclusion || run.status;
                  return (
                    <tr key={run.id} className="hover:bg-surface-50 dark:hover:bg-slate-800/30 transition-colors">
                      <td className="px-3 py-3">
                        {conclusionOrStatus === "success" ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        ) : conclusionOrStatus === "failure" ? (
                          <XCircle className="h-4 w-4 text-red-500" />
                        ) : conclusionOrStatus === "in_progress" ? (
                          <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />
                        ) : (
                          <Clock className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                        )}
                      </td>
                      <td className="px-3 py-3 text-slate-900 dark:text-slate-100 max-w-[200px] truncate">{run.workflow_name}</td>
                      <td className="px-3 py-3 text-slate-600 dark:text-slate-400 text-xs">{run.repository_name ?? "unknown"}</td>
                      <td className="px-3 py-3">
                        {run.head_branch ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400">{run.head_branch}</span>
                        ) : <span className="text-slate-400 dark:text-slate-500">—</span>}
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-500 dark:text-slate-400 capitalize">{run.event ?? "—"}</td>
                      <td className="px-3 py-3"><StatusBadge status={conclusionOrStatus ?? "unknown"} /></td>
                      <td className="px-3 py-3 text-slate-600 dark:text-slate-400 text-xs">{run.actor_login ?? "—"}</td>
                      <td className="px-3 py-3 text-slate-500 dark:text-slate-400 text-xs">{formatDuration(run.duration_seconds)}</td>
                      <td className="px-3 py-3 text-slate-500 dark:text-slate-400 text-xs whitespace-nowrap">
                        {run.started_at ? formatDistanceToNow(new Date(run.started_at), { addSuffix: true }) : "—"}
                      </td>
                      <td className="px-3 py-3">
                        {run.html_url && (
                          <a href={run.html_url} target="_blank" rel="noopener noreferrer" className="text-slate-400 dark:text-slate-500 hover:text-brand-600 dark:hover:text-brand-400 transition-colors">
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-slate-200/60 dark:border-slate-800/80 flex items-center justify-between text-sm">
              <span className="text-slate-500 dark:text-slate-400">Page {page} of {totalPages} ({total} total)</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-1 text-sm text-slate-700 dark:text-slate-300 hover:bg-surface-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-1 text-sm text-slate-700 dark:text-slate-300 hover:bg-surface-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
